/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { defineMessages, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import React, { memo } from 'react';
import isNil from 'lodash/isNil';
import copy from 'copy-to-clipboard';
import { Copy16 } from '@carbon/icons-react';

import styles from './Property.module.css';

import { handleKeyDown as utilHandleKeyDown } from 'Utils';

const messages = defineMessages({
  copyToClipboard: 'Copy to clipboard',
  copied: 'Copied!',
  iconDescription: 'Copy to clipboard',
});

const propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSecret: PropTypes.bool,
};

const defaultProps = {
  isSecret: false,
  value: null,
};

/** Read only rendered of label and value, style does not match carbon styles */
const Property = memo(({ isSecret, intl: { formatMessage }, value, label }) => {
  const copyToClipboard = () => {
    copy(value);
  };

  const handleKeyDown = event => {
    utilHandleKeyDown(event, copyToClipboard());
  };

  return (
    <div className={styles.propertyContainer}>
      <div className={styles.propertyLabel}>{label}</div>
      {!isNil(value) ? (
        <div
          className={styles.propertyValue}
          onKeyDown={e => handleKeyDown(e)}
          role="button"
          aria-label={formatMessage(messages.copyToClipboard)}
          tabIndex="0"
          onClick={() => copyToClipboard()}>
          <span className={styles.propertyValueSpan}>
            {!isSecret ? value : formatMessage(messages.copyToClipboard)}
          </span>
          <Copy16 className="bx--snippet__icon" />
        </div>
      ) : null}
    </div>
  );
});

Property.propTypes = propTypes;
Property.defaultProps = defaultProps;
export default injectIntl(Property);
